import { Accordion } from '@material-ui/core';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect, useState } from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import useRequest from '@swf/core/hooks/useRequest';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { ServerMessageHandler } from '@swf/notification';

import ToggleArrow from '../../assets/icons/arrow.svg';
import BonusSummaryIcon from '../../assets/icons/bonus-icon.svg';
import createSingleton from '../../hooks/useSingleton';
import type { BonusInfo } from '../../types/Cashier';
import { trimWithoutRounding } from '../../utils/cashier';

import {
  TXTWCL_Bonus_Summary,
  TXTWCL_You_are_making_a_deposit_of,
  TXTWCL_Your_selected_bonus
} from './BonusSummary.messages.json';
import styles from './BonusSummary.scss';

const summaryPage = 'DepositSummary';
const methodPage = 'DepositMethod';
const [getValue, setValue] = createSingleton(true);

interface Props {
  page?: string;
}

const BonusSummary: React.FC<Props> = ({ page }) => {
  const isClosed = getValue();
  const request = useRequest();
  const [localDescription, setLocalDescription] = useState('');
  const [showSummary, setShowSummary] = useState(false);

  const {
    data,
    values: { deposit }
  } = useWizardContext();

  const showAmountSummary = page === summaryPage;

  if (deposit?.bonusCode && !data.bonusCodeDetails) {
    data.bonusCodeDetails = [];
  }

  const bonusDescriptionAvailable = data.bonusCodeDetails?.find(
    ({ code }) => code === deposit?.bonusCode || code === data?.previousBonusCode
  );

  useEffect(() => {
    if (!deposit?.bonusCode && !(page === methodPage && data?.previousBonusCode)) {
      setShowSummary(false);
      return;
    }

    if (!bonusDescriptionAvailable?.code) {
      request<BonusInfo>({
        service: 'wc3.bonus.getCodeInfo',
        body: { bonusCode: deposit.bonusCode }
      }).subscribe({
        next: ({ body: { description } }) => {
          data.bonusCodeDetails?.push({ code: deposit.bonusCode || '', description });
          setLocalDescription(description);
          setShowSummary(true);
        },
        error: () => {
          setShowSummary(false);
        }
      });
    } else {
      setLocalDescription(bonusDescriptionAvailable.description);
      setShowSummary(true);
    }
  }, [
    bonusDescriptionAvailable?.code,
    bonusDescriptionAvailable?.description,
    data.bonusCodeDetails,
    deposit?.bonusCode,
    data?.previousBonusCode,
    page,
    request
  ]);

  return (
    showSummary && (
      <Accordion
        expanded={!isClosed}
        classes={{ root: styles.container, expanded: styles.containerExpanded }}
      >
        <div className={styles.header}>
          <p className={styles.title} onClick={() => setValue(!isClosed)}>
            <BonusSummaryIcon className={styles.bonusIcon} />
            <Translation {...TXTWCL_Bonus_Summary} />
            <span
              className={classNames(styles.toggleArrow, {
                [styles.toggleArrowActive]: !isClosed
              })}
            >
              <ToggleArrow />
            </span>
          </p>
        </div>
        <div className={styles.body}>
          {showAmountSummary && (
            <div className={styles.depositInfo}>
              <p className={styles.depositInfoTitle}>
                <Translation {...TXTWCL_You_are_making_a_deposit_of} />
              </p>
              <p className={styles.depositInfoSum}>
                <CurrencyAmount
                  value={trimWithoutRounding(deposit.amount)}
                  currency={deposit.currency}
                  dollarValue
                />
              </p>
            </div>
          )}
          <div>
            <p className={styles.bonusInfoTitle}>
              <Translation {...TXTWCL_Your_selected_bonus} />
            </p>
            <div className={styles.bonusInfoBody}>
              {localDescription && <ServerMessageHandler message={localDescription} />}
            </div>
          </div>
        </div>
      </Accordion>
    )
  );
};

export default BonusSummary;
